import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Play, Image, Zap, Layers, ArrowRight, Check, Sparkles, Video, Palette, BarChart3, ChevronRight, ChevronLeft, FlaskConical, Globe, Trophy, MousePointerClick, Clock, Wand2, Camera, Scissors, Type, Upload, Tv, Box, Star, Users, ShoppingCart, Send } from 'lucide-react';

const NAV_LINKS = [
  { label: 'Tools', href: '#tools' },
  { label: 'Presets', href: '#presets' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'Pricing', href: '#pricing' },
];

// ── TOOLS GRID (Higgsfield-style) ──
const TOOLS = [
  { name: 'Create Image', desc: 'Generate AI images', icon: Image, img: 'https://images.unsplash.com/photo-1560343090-f0409e92791a?w=300&h=200&fit=crop', color: '#CDFE65', badge: null },
  { name: 'Create Video', desc: 'Generate AI videos', icon: Video, img: 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=300&h=200&fit=crop', color: '#06B6D4', badge: null },
  { name: 'Click to Ad', desc: 'URL to ad creatives', icon: Globe, img: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=300&h=200&fit=crop', color: '#F59E0B', badge: 'PRO' },
  { name: 'A/B Testing', desc: 'Test & auto-optimize', icon: FlaskConical, img: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=300&h=200&fit=crop', color: '#7C3AED', badge: 'NEW' },
  { name: 'Brand Kits', desc: 'Stay on-brand', icon: Palette, img: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=300&h=200&fit=crop', color: '#EF4444', badge: null },
  { name: 'Batch Generate', desc: 'All platforms at once', icon: Layers, img: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=300&h=200&fit=crop', color: '#10B981', badge: null },
  { name: 'Upscale', desc: 'Enhance media quality', icon: Zap, img: 'https://images.unsplash.com/photo-1585386959984-a4155224a1ad?w=300&h=200&fit=crop', color: '#EC4899', badge: 'SOON' },
  { name: 'Lipsync', desc: 'Create talking clips', icon: Tv, img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=200&fit=crop', color: '#F97316', badge: 'SOON' },
  { name: 'Motion Control', desc: 'Control character actions', icon: Camera, img: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=300&h=200&fit=crop', color: '#14B8A6', badge: 'SOON' },
  { name: 'Face Swap', desc: 'AI face swap for ads', icon: Users, img: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=300&h=200&fit=crop', color: '#8B5CF6', badge: 'SOON' },
];

// ── PRESET CATEGORIES (Higgsfield-style horizontal scroll) ──
const PRESET_CATEGORIES = [
  {
    name: 'Ad Templates', color: '#CDFE65', presets: [
      { name: 'Product Reveal', img: 'https://images.unsplash.com/photo-1526170375885-4d8ecf77b99f?w=280&h=280&fit=crop' },
      { name: 'UGC Testimonial', img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=280&h=280&fit=crop' },
      { name: 'Hook Pattern', img: 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=280&h=280&fit=crop' },
      { name: 'Lifestyle Shot', img: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=280&h=280&fit=crop' },
      { name: 'Before/After', img: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=280&h=280&fit=crop' },
      { name: 'App Demo', img: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=280&h=280&fit=crop' },
      { name: 'Unboxing', img: 'https://images.unsplash.com/photo-1600185365926-3a2ce3cdb9eb?w=280&h=280&fit=crop' },
      { name: 'Story Cover', img: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=280&h=280&fit=crop' },
    ]
  },
  {
    name: 'Visual Effects', color: '#7C3AED', presets: [
      { name: 'Explosion', img: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=280&h=280&fit=crop' },
      { name: 'Particle Burst', img: 'https://images.unsplash.com/photo-1534796636912-3b95b3ab5986?w=280&h=280&fit=crop' },
      { name: 'Glitch Effect', img: 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=280&h=280&fit=crop' },
      { name: 'Neon Glow', img: 'https://images.unsplash.com/photo-1557672172-298e090bd0f1?w=280&h=280&fit=crop' },
      { name: 'Smoke Reveal', img: 'https://images.unsplash.com/photo-1518834107812-67b0b7c58434?w=280&h=280&fit=crop' },
      { name: 'Liquid Metal', img: 'https://images.unsplash.com/photo-1567095761054-7a02e69e5c43?w=280&h=280&fit=crop' },
      { name: 'Fire Element', img: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0afa?w=280&h=280&fit=crop' },
      { name: 'Ice Crystal', img: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=280&h=280&fit=crop' },
    ]
  },
  {
    name: 'Camera Moves', color: '#06B6D4', presets: [
      { name: '360 Orbit', img: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=280&h=280&fit=crop' },
      { name: 'Dolly Zoom', img: 'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=280&h=280&fit=crop' },
      { name: 'Crash Zoom In', img: 'https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=280&h=280&fit=crop' },
      { name: 'FPV Drone', img: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=280&h=280&fit=crop' },
      { name: 'Bullet Time', img: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=280&h=280&fit=crop' },
      { name: 'Aerial Pullback', img: 'https://images.unsplash.com/photo-1477346611705-65d1883cee1e?w=280&h=280&fit=crop' },
      { name: 'Jib Up', img: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=280&h=280&fit=crop' },
      { name: 'Arc Shot', img: 'https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?w=280&h=280&fit=crop' },
    ]
  },
  {
    name: 'Social & UGC', color: '#F59E0B', presets: [
      { name: 'Selfie Pose', img: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=280&h=280&fit=crop' },
      { name: 'Outfit Check', img: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=280&h=280&fit=crop' },
      { name: 'Morning Routine', img: 'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=280&h=280&fit=crop' },
      { name: 'Mukbang', img: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=280&h=280&fit=crop' },
      { name: 'Get Ready', img: 'https://images.unsplash.com/photo-1487222477894-8943e31ef7b2?w=280&h=280&fit=crop' },
      { name: 'Static Posing', img: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=280&h=280&fit=crop' },
      { name: 'Pool Jump', img: 'https://images.unsplash.com/photo-1530549387789-4c1017266635?w=280&h=280&fit=crop' },
      { name: 'Red Carpet', img: 'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=280&h=280&fit=crop' },
    ]
  },
];

const GALLERY_ITEMS = [
  { url: 'https://images.unsplash.com/photo-1652202089978-805c932a7ef1?w=500&h=500&fit=crop', label: 'Cyberpunk City', type: 'video', model: 'Kling 3.0' },
  { url: 'https://images.unsplash.com/photo-1741514374662-a597fe342fea?w=500&h=500&fit=crop', label: 'Fashion Portrait', type: 'image', model: 'FLUX 2 Pro' },
  { url: 'https://images.unsplash.com/photo-1737651704014-ed9e926333cf?w=500&h=500&fit=crop', label: 'Product Shot', type: 'image', model: 'FLUX 2 Pro' },
  { url: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=500&h=500&fit=crop', label: 'Watch Campaign', type: 'image', model: 'Recraft V3' },
  { url: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=500&h=500&fit=crop', label: 'Sneaker Ad', type: 'image', model: 'Ideogram 3' },
  { url: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=500&h=500&fit=crop', label: 'Headphones', type: 'video', model: 'MiniMax' },
  { url: 'https://images.unsplash.com/photo-1585386959984-a4155224a1ad?w=500&h=500&fit=crop', label: 'Perfume Luxury', type: 'image', model: 'FLUX 2 Pro' },
  { url: 'https://images.unsplash.com/photo-1600185365926-3a2ce3cdb9eb?w=500&h=500&fit=crop', label: 'Sneaker Flat Lay', type: 'image', model: 'Nano Banana' },
  { url: 'https://images.unsplash.com/photo-1491553895911-0055eca6402d?w=500&h=500&fit=crop', label: 'Nike Air Max', type: 'video', model: 'Kling 3.0' },
  { url: 'https://images.unsplash.com/photo-1526170375885-4d8ecf77b99f?w=500&h=500&fit=crop', label: 'Sunglasses', type: 'image', model: 'Seedream 4.5' },
  { url: 'https://images.unsplash.com/photo-1560343090-f0409e92791a?w=500&h=500&fit=crop', label: 'Camera Product', type: 'image', model: 'FLUX 2 Pro' },
  { url: 'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=500&h=500&fit=crop', label: 'Studio Scene', type: 'video', model: 'Kling 3.0' },
];

const PLANS = [
  { name: 'Free', price: '$0', period: '/forever', gens: '10', features: ['10 generations/month', '2 AI models', 'Basic templates', 'Local storage'], cta: 'Get Started', popular: false },
  { name: 'Starter', price: '$29', period: '/month', gens: '100', features: ['100 generations/month', 'All AI models', 'All templates & presets', 'Brand kits', 'Batch processing'], cta: 'Start Free Trial', popular: false },
  { name: 'Growth', price: '$79', period: '/month', gens: '500', features: ['500 generations/month', 'All AI models', 'All templates & presets', 'Unlimited brand kits', 'A/B testing', 'Priority rendering'], cta: 'Start Free Trial', popular: true },
  { name: 'Scale', price: '$199', period: '/month', gens: '2,000', features: ['2,000 generations/month', 'All AI models', 'Unlimited everything', 'Click-to-Ad', 'API access', 'Dedicated support'], cta: 'Contact Sales', popular: false },
];

function HorizontalScroll({ children, className = '' }) {
  const ref = useRef(null);
  const scroll = (dir) => { ref.current?.scrollBy({ left: dir * 300, behavior: 'smooth' }); };
  return (
    <div className={`relative group ${className}`}>
      <button onClick={() => scroll(-1)} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-black/60 backdrop-blur rounded-full flex items-center justify-center text-white/60 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Scroll left"><ChevronLeft className="w-5 h-5" /></button>
      <div ref={ref} className="flex gap-3 overflow-x-auto scrollbar-hide pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>{children}</div>
      <button onClick={() => scroll(1)} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-black/60 backdrop-blur rounded-full flex items-center justify-center text-white/60 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Scroll right"><ChevronRight className="w-5 h-5" /></button>
    </div>
  );
}

export default function LandingPage() {
  const { user } = useAuth();
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistEmail) return;
    setWaitlistLoading(true);
    try {
      const res = await api.joinWaitlist({ email: waitlistEmail, source: 'landing' });
      toast.success(res.message);
      setWaitlistEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join waitlist');
    } finally { setWaitlistLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#08080A] text-white overflow-x-hidden">
      <div className="noise-overlay" />

      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 glass" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Sparkles className="w-6 h-6 text-[#CDFE65]" />
            <span className="font-black text-xl tracking-tight" style={{ fontFamily: 'Chivo' }}>THE HUMAN</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map(l => (
              <a key={l.label} href={l.href} className="text-sm text-white/60 hover:text-[#CDFE65] transition-colors">{l.label}</a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard" className="btn-primary text-sm" data-testid="go-dashboard-btn">Dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="text-sm text-white/60 hover:text-white transition-colors" data-testid="login-link">Log in</Link>
                <Link to="/signup" className="btn-primary text-sm" data-testid="signup-btn">Get Started</Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-6" data-testid="hero-section">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#CDFE65]/5 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-[#7C3AED]/10 rounded-full blur-[80px]" />
        </div>
        <div className="max-w-7xl mx-auto relative">
          <div className="max-w-4xl">
            <p className="text-xs tracking-[0.3em] uppercase text-[#CDFE65] font-mono mb-6 animate-fade-in-up opacity-0">AI Creative Engine for Brands</p>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.9] uppercase mb-8 animate-fade-in-up opacity-0 stagger-1" style={{ fontFamily: 'Chivo' }}>
              Generate<br /><span className="text-[#CDFE65]">Anything.</span><br />Ship Everywhere.
            </h1>
            <p className="text-lg md:text-xl text-white/50 max-w-2xl leading-relaxed mb-10 animate-fade-in-up opacity-0 stagger-2" style={{ fontFamily: 'Manrope' }}>
              AI-powered video and image generation for performance marketers. Brand-consistent creatives at scale, across every platform.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up opacity-0 stagger-3">
              <Link to="/signup" className="btn-primary inline-flex items-center gap-2 text-base" data-testid="hero-cta-btn">
                Launch Studio <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#tools" className="btn-secondary inline-flex items-center gap-2 text-base" data-testid="hero-tools-btn">
                Explore Tools
              </a>
            </div>
          </div>
          <div className="mt-12 grid grid-cols-4 gap-4 max-w-lg animate-fade-in-up opacity-0 stagger-4">
            {[['10+', 'AI Models'], ['50+', 'Presets'], ['5', 'Platforms'], ['12', 'Templates']].map(([num, label]) => (
              <div key={label} className="text-center">
                <div className="text-2xl font-black text-[#CDFE65]" style={{ fontFamily: 'Chivo' }}>{num}</div>
                <div className="text-[10px] text-white/40 uppercase tracking-wider">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Multi-Platform Distribution — high visibility */}
      <section className="py-20 px-6" data-testid="distribute-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#06B6D4] font-mono mb-4">Multi-Platform Distribution</p>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Chivo' }}>One click.<br /><span className="text-[#06B6D4]">Every platform.</span></h2>
              <p className="text-lg text-white/50 leading-relaxed mb-8">
                Deploy your ad creatives across Instagram, TikTok, Facebook, YouTube, Google Ads, LinkedIn, X, Pinterest, Snapchat, and email — auto-formatted for each platform's specs.
              </p>
              <div className="grid grid-cols-5 gap-3 mb-8">
                {[
                  { name: 'Instagram', icon: 'https://cdn.simpleicons.org/instagram/E4405F' },
                  { name: 'TikTok', icon: 'https://cdn.simpleicons.org/tiktok/ffffff' },
                  { name: 'YouTube', icon: 'https://cdn.simpleicons.org/youtube/FF0000' },
                  { name: 'Google', icon: 'https://cdn.simpleicons.org/googleads/4285F4' },
                  { name: 'LinkedIn', icon: 'https://cdn.simpleicons.org/linkedin/0A66C2' },
                  { name: 'Facebook', icon: 'https://cdn.simpleicons.org/facebook/1877F2' },
                  { name: 'X', icon: 'https://cdn.simpleicons.org/x/ffffff' },
                  { name: 'Pinterest', icon: 'https://cdn.simpleicons.org/pinterest/BD081C' },
                  { name: 'Snapchat', icon: 'https://cdn.simpleicons.org/snapchat/FFFC00' },
                  { name: 'Email', icon: null },
                ].map((p) => (
                  <div key={p.name} className="flex flex-col items-center gap-1.5 p-2 rounded-lg bg-white/[0.02] border border-white/5 hover:border-white/20 transition-colors">
                    {p.icon ? <img src={p.icon} alt={p.name} className="w-5 h-5 opacity-70" loading="lazy" /> : <Send className="w-5 h-5 text-white/30" />}
                    <span className="text-[9px] text-white/40">{p.name}</span>
                  </div>
                ))}
              </div>
              <Link to="/signup" className="btn-primary inline-flex items-center gap-2" data-testid="distribute-cta-btn">
                Start Distributing <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="bg-[#0F0F11] border border-white/5 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-5">
                <Send className="w-5 h-5 text-[#06B6D4]" />
                <span className="font-bold">Q1 Product Launch</span>
                <span className="text-[10px] px-2 py-0.5 bg-green-500/10 text-green-400 rounded-full font-bold uppercase">Deployed</span>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-5">
                {[{ label: 'Platforms', value: '8' }, { label: 'Formats', value: '24' }, { label: 'Live', value: '23' }].map(s => (
                  <div key={s.label} className="bg-white/[0.02] rounded-lg p-3 text-center">
                    <div className="text-xl font-black" style={{ fontFamily: 'Chivo' }}>{s.value}</div>
                    <p className="text-[10px] text-white/30 uppercase">{s.label}</p>
                  </div>
                ))}
              </div>
              {[
                { name: 'Instagram', formats: '3 formats', status: 'live' },
                { name: 'TikTok', formats: '2 formats', status: 'live' },
                { name: 'Google Ads', formats: '5 formats', status: 'live' },
                { name: 'YouTube', formats: '3 formats', status: 'live' },
                { name: 'LinkedIn', formats: '2 formats', status: 'live' },
              ].map(p => (
                <div key={p.name} className="flex items-center justify-between p-2.5 bg-white/[0.02] rounded-lg mb-1.5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-2 h-2 rounded-full bg-green-400" />
                    <span className="text-xs font-medium">{p.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-white/30 font-mono">{p.formats}</span>
                    <span className="text-[9px] text-green-400 font-bold uppercase">{p.status}</span>
                  </div>
                </div>
              ))}
              <p className="text-[10px] text-white/20 mt-3 text-center font-mono">Auto-formatted for each platform's ad specs</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tools Grid (Higgsfield-style) */}
      <section id="tools" className="py-16 px-6" data-testid="tools-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#CDFE65] font-mono mb-2">Tools</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Chivo' }}>Your creative studio</h2>
            </div>
            <Link to="/signup" className="hidden md:inline-flex text-sm text-[#CDFE65] hover:underline items-center gap-1">Explore all tools <ChevronRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {TOOLS.map((tool) => (
              <Link to="/signup" key={tool.name} className="group relative bg-[#0F0F11] border border-white/5 rounded-xl overflow-hidden card-hover" data-testid={`tool-${tool.name.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img src={tool.img} alt={tool.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F11] via-transparent to-transparent" />
                  {tool.badge && (
                    <span className={`absolute top-2 right-2 text-[9px] px-2 py-0.5 rounded font-bold uppercase ${tool.badge === 'NEW' ? 'bg-[#CDFE65] text-black' : tool.badge === 'PRO' ? 'bg-[#7C3AED] text-white' : 'bg-white/10 text-white/50'}`}>{tool.badge}</span>
                  )}
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-2 mb-0.5">
                    <tool.icon className="w-3.5 h-3.5" style={{ color: tool.color }} />
                    <span className="font-bold text-sm">{tool.name}</span>
                  </div>
                  <p className="text-[11px] text-white/40">{tool.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Presets & Templates (Higgsfield-style horizontal scroll) */}
      <section id="presets" className="py-16 px-6 bg-[#0F0F11]/30" data-testid="presets-section">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs tracking-[0.3em] uppercase text-[#7C3AED] font-mono mb-2">Presets & Effects</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-10" style={{ fontFamily: 'Chivo' }}>Ready-made creative recipes</h2>

          {PRESET_CATEGORIES.map((cat) => (
            <div key={cat.name} className="mb-10" data-testid={`preset-category-${cat.name.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: cat.color }} />
                  {cat.name}
                </h3>
                <Link to="/signup" className="text-xs text-white/30 hover:text-white/60 flex items-center gap-1">View all <ChevronRight className="w-3 h-3" /></Link>
              </div>
              <HorizontalScroll>
                {cat.presets.map((p) => (
                  <Link to="/signup" key={p.name} className="shrink-0 w-40 md:w-48 group" data-testid={`preset-${p.name.toLowerCase().replace(/\s/g, '-')}`}>
                    <div className="relative overflow-hidden rounded-xl border border-white/5 mb-2">
                      <img src={p.img} alt={p.name} className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <Play className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                      </div>
                    </div>
                    <p className="text-xs font-medium text-white/70 group-hover:text-white transition-colors truncate">{p.name}</p>
                  </Link>
                ))}
              </HorizontalScroll>
            </div>
          ))}
        </div>
      </section>

      {/* Gallery Grid */}
      <section id="gallery" className="py-16 px-6" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs tracking-[0.3em] uppercase text-[#CDFE65] font-mono mb-2">Community</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Chivo' }}>Made with The Human</h2>
          <p className="text-sm text-white/40 mb-8">Real examples generated by our AI models in seconds.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {GALLERY_ITEMS.map((item, i) => (
              <div key={i} className="relative group overflow-hidden rounded-xl border border-white/5 card-hover" data-testid={`gallery-item-${i}`}>
                <img src={item.url} alt={item.label} className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${item.type === 'video' ? 'bg-[#CDFE65]/20 text-[#CDFE65]' : 'bg-[#06B6D4]/20 text-[#06B6D4]'}`}>{item.type}</span>
                </div>
                <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-xs font-medium">{item.label}</p>
                  <p className="text-[10px] font-mono text-white/40">{item.model}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link to="/signup" className="btn-secondary inline-flex items-center gap-2 text-sm">Generate your own <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* Smart A/B Testing */}
      <section id="ab-testing" className="py-20 px-6 bg-[#0F0F11]/50" data-testid="ab-testing-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#7C3AED] font-mono mb-4">Smart A/B Testing</p>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Chivo' }}>Don't guess.<br /><span className="text-[#7C3AED]">Test.</span></h2>
              <p className="text-lg text-white/50 leading-relaxed mb-8">
                Generate multiple creative variants, then let data decide the winner. Track impressions, clicks, and conversions per variant with statistical confidence.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  { icon: FlaskConical, text: 'Create tests with 2-4 creative variants', color: '#7C3AED' },
                  { icon: BarChart3, text: 'Track CTR, conversions, and engagement in real-time', color: '#CDFE65' },
                  { icon: Trophy, text: 'Auto-select winners with statistical significance', color: '#F59E0B' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${item.color}15` }}>
                      <item.icon className="w-5 h-5" style={{ color: item.color }} />
                    </div>
                    <p className="text-sm text-white/70">{item.text}</p>
                  </div>
                ))}
              </div>
              <Link to="/signup" className="btn-primary inline-flex items-center gap-2" data-testid="ab-cta-btn">
                Start Testing <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="bg-[#0F0F11] border border-white/5 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-5">
                <FlaskConical className="w-5 h-5 text-[#7C3AED]" />
                <span className="font-bold">Homepage Hero Test</span>
                <span className="text-[10px] px-2 py-0.5 bg-green-500/10 text-green-400 rounded-full font-bold uppercase">Active</span>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-5">
                {[{ label: 'Impressions', value: '12,847' }, { label: 'Clicks', value: '1,024' }, { label: 'Conversions', value: '156' }].map(s => (
                  <div key={s.label} className="bg-white/[0.02] rounded-lg p-3 text-center">
                    <div className="text-lg font-black" style={{ fontFamily: 'Chivo' }}>{s.value}</div>
                    <p className="text-[10px] text-white/30 uppercase">{s.label}</p>
                  </div>
                ))}
              </div>
              {[
                { label: 'Variant A', ctr: 8.2, bar: 82, winner: true },
                { label: 'Variant B', ctr: 6.1, bar: 61, winner: false },
                { label: 'Variant C', ctr: 4.9, bar: 49, winner: false },
              ].map(v => (
                <div key={v.label} className={`flex items-center gap-3 p-3 rounded-lg mb-2 ${v.winner ? 'bg-[#CDFE65]/5 border border-[#CDFE65]/20' : 'bg-white/[0.02]'}`}>
                  <span className="text-xs font-bold w-20">{v.label}</span>
                  <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${v.winner ? 'bg-[#CDFE65]' : 'bg-white/20'}`} style={{ width: `${v.bar}%` }} />
                  </div>
                  <span className={`text-sm font-bold w-14 text-right ${v.winner ? 'text-[#CDFE65]' : 'text-white/40'}`}>{v.ctr}%</span>
                  {v.winner && <Trophy className="w-4 h-4 text-[#CDFE65]" />}
                </div>
              ))}
              <p className="text-[10px] text-white/20 mt-3 text-center font-mono">Winner auto-selected at 95% statistical confidence</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs tracking-[0.3em] uppercase text-[#CDFE65] font-mono mb-4">Pricing</p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-16" style={{ fontFamily: 'Chivo' }}>Simple, transparent pricing</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PLANS.map((p) => (
              <div key={p.name} className={`relative bg-[#0F0F11] border ${p.popular ? 'border-[#CDFE65]' : 'border-white/5'} p-6 rounded-2xl card-hover`} data-testid={`plan-card-${p.name.toLowerCase()}`}>
                {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#CDFE65] text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">Popular</div>}
                <h3 className="text-lg font-bold mb-1">{p.name}</h3>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-black" style={{ fontFamily: 'Chivo' }}>{p.price}</span>
                  <span className="text-sm text-white/40">{p.period}</span>
                </div>
                <p className="text-sm text-white/40 mb-6">{p.gens} generations/mo</p>
                <ul className="space-y-2 mb-6">
                  {p.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-white/60">
                      <Check className="w-3.5 h-3.5 text-[#CDFE65] shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <Link to="/signup" className={`block text-center text-sm py-2.5 rounded-full font-bold transition-all ${p.popular ? 'btn-primary' : 'btn-secondary'}`} data-testid={`plan-cta-${p.name.toLowerCase()}`}>
                  {p.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Waitlist CTA */}
      <section className="py-20 px-6 bg-[#0F0F11]/50" data-testid="waitlist-section">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-6" style={{ fontFamily: 'Chivo' }}>Ready to <span className="text-[#CDFE65]">create</span>?</h2>
          <p className="text-lg text-white/50 mb-10">Join the waitlist for early access to The Human AI.</p>
          <form onSubmit={handleWaitlist} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input type="email" value={waitlistEmail} onChange={e => setWaitlistEmail(e.target.value)} placeholder="your@email.com" className="forge-input flex-1" data-testid="waitlist-email-input" required />
            <button type="submit" disabled={waitlistLoading} className="btn-primary text-sm whitespace-nowrap" data-testid="waitlist-submit-btn">
              {waitlistLoading ? 'Joining...' : 'Join Waitlist'}
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5" data-testid="footer">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#CDFE65]" />
            <span className="font-black tracking-tight" style={{ fontFamily: 'Chivo' }}>THE HUMAN</span>
          </div>
          <p className="text-sm text-white/30">Built for performance marketers. Powered by AI. <span className="text-white/50">thehuman.ai</span></p>
        </div>
      </footer>
    </div>
  );
}
